import { defineComponent, type PropType, ref, watch, onUnmounted, Transition } from 'vue'
import { IconsPreviewClose } from '#components'

interface TouchPoint {
  x: number
  y: number
}

export default defineComponent({
  name: 'BaseImagePreview',
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'close'],
  setup(props, { emit }) {
    const imageRef = ref<HTMLImageElement>()
    const containerRef = ref<HTMLDivElement>()

    // Transform states
    const scale = ref(1)
    const translateX = ref(0)
    const translateY = ref(0)
    const isAnimating = ref(false)

    // Touch handling
    const initialDistance = ref(0)
    const initialScale = ref(1)
    const lastTouchPoint = ref<TouchPoint | null>(null)
    const touchStartTime = ref(0)

    // Calculate distance between two touch points
    const getDistance = (touch1: Touch, touch2: Touch): number => {
      const dx = touch1.clientX - touch2.clientX
      const dy = touch1.clientY - touch2.clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    // Get center point of two touches
    const getCenter = (touch1: Touch, touch2: Touch): TouchPoint => {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2
      }
    }

    // Handle touch start
    const handleTouchStart = (e: TouchEvent) => {
      // e.preventDefault()
      touchStartTime.value = Date.now()

      if (e.touches.length === 2) {
        // Pinch zoom start
        initialDistance.value = getDistance(e.touches[0], e.touches[1])
        initialScale.value = scale.value
      } else if (e.touches.length === 1) {
        // Pan start
        lastTouchPoint.value = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        }
      }
    }

    // Handle touch move
    const handleTouchMove = (e: TouchEvent) => {
      // e.preventDefault()
      e.stopPropagation()

      if (e.touches.length === 2) {
        // Pinch zoom
        const currentDistance = getDistance(e.touches[0], e.touches[1])
        const scaleChange = currentDistance / initialDistance.value
        const newScale = Math.max(0.5, Math.min(3, initialScale.value * scaleChange))
        scale.value = newScale
      } else if (e.touches.length === 1 && lastTouchPoint.value) {
        // Pan
        const deltaX = e.touches[0].clientX - lastTouchPoint.value.x
        const deltaY = e.touches[0].clientY - lastTouchPoint.value.y

        translateX.value += deltaX
        translateY.value += deltaY

        lastTouchPoint.value = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        }
      }
    }

    // Handle touch end
    const handleTouchEnd = (e: TouchEvent) => {
      const touchDuration = Date.now() - touchStartTime.value

      // Double tap to reset
      if (e.touches.length === 0 && touchDuration < 300) {
        if (scale.value !== 1) {
          resetTransform()
        }
      }

      lastTouchPoint.value = null
    }

    // Mouse wheel zoom
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = Math.max(0.5, Math.min(3, scale.value * delta))
      scale.value = newScale
    }

    // Reset transform with animation
    const resetTransform = () => {
      isAnimating.value = true
      scale.value = 1
      translateX.value = 0
      translateY.value = 0
      setTimeout(() => {
        isAnimating.value = false
      }, 300)
    }

    // Close preview
    const handleClose = () => {
      emit('update:visible', false)
      emit('close')
    }

    // Reset after transition ends
    const handleAfterLeave = () => {
      scale.value = 1
      translateX.value = 0
      translateY.value = 0
      isAnimating.value = false
    }

    // Handle overlay click
    const handleOverlayClick = (e: MouseEvent) => {
      if (e.target === containerRef.value) {
        handleClose()
      }
    }

    // Handle body scroll on open/close
    watch(
      () => props.visible,
      (visible) => {
        if (visible) {
          // Prevent body scroll and pointer events
          document.body.style.overflow = 'hidden'
          document.body.style.pointerEvents = 'none'
          // Re-enable pointer events after a brief delay to allow animation
          setTimeout(() => {
            if (props.visible) {
              document.body.style.pointerEvents = ''
            }
          }, 50)
        } else {
          // Restore body scroll and pointer events
          document.body.style.overflow = ''
          document.body.style.pointerEvents = ''
        }
      }
    )

    // Cleanup on unmount
    onUnmounted(() => {
      document.body.style.overflow = ''
      document.body.style.pointerEvents = ''
    })

    return () => (
      <Transition
        name="image-preview"
        onAfterLeave={handleAfterLeave}
        css={false}
        onEnter={(el: Element) => {
          const element = el as HTMLElement
          element.style.opacity = '0'
          element.style.transform = 'scale(0.95)'
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              element.style.transition = 'opacity 250ms ease-out, transform 250ms ease-out'
              element.style.opacity = '1'
              element.style.transform = 'scale(1)'
            })
          })
        }}
        onLeave={(el: Element, done: () => void) => {
          const element = el as HTMLElement
          element.style.transition = 'opacity 200ms ease-in, transform 200ms ease-in'
          element.style.opacity = '0'
          element.style.transform = 'scale(0.95)'
          setTimeout(done, 200)
        }}
      >
        {props.visible && (
          <div
            ref={containerRef}
            class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={handleOverlayClick}
            style={{ touchAction: 'none' }}
          >
            {/* Image container */}
            <div
              class="relative h-full w-full overflow-hidden"
              onTouchstart={handleTouchStart}
              onTouchmove={handleTouchMove}
              onTouchend={handleTouchEnd}
              onWheel={handleWheel}
              style={{ touchAction: 'none' }}
            >
              <img
                ref={imageRef}
                src={props.src}
                alt={props.alt}
                class={[
                  'absolute left-1/2 top-1/2 max-h-full max-w-full select-none object-contain',
                  isAnimating.value ? 'transition-transform duration-300 ease-out' : 'transition-none'
                ]}
                style={{
                  transform: `translate(calc(-50% + ${translateX.value}px), calc(-50% + ${translateY.value}px)) scale(${scale.value})`,
                  transformOrigin: 'center center',
                  willChange: 'transform'
                }}
                draggable={false}
              />
            </div>

            {/* Close button - 右下角 */}

            {props.visible && (
              <button
                class="fixed bottom-5.5 right-6 z-10 flex h-18 w-18 items-center justify-center rounded-full bg-[#363A41] transition-transform"
                onClick={handleClose}
                aria-label="Close preview"
              >
                <IconsPreviewClose class="w-9.5 h-9.5" />
              </button>
            )}

            {/* Scale indicator */}
            <Transition
              css={false}
              onEnter={(el: Element) => {
                const element = el as HTMLElement
                element.style.opacity = '0'
                element.style.transform = 'translateY(16px)'
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    element.style.transition = 'opacity 300ms ease-out 150ms, transform 300ms ease-out 150ms'
                    element.style.opacity = '1'
                    element.style.transform = 'translateY(0)'
                  })
                })
              }}
              onLeave={(el: Element, done: () => void) => {
                const element = el as HTMLElement
                element.style.transition = 'opacity 150ms ease-in, transform 150ms ease-in'
                element.style.opacity = '0'
                element.style.transform = 'translateY(8px)'
                setTimeout(done, 150)
              }}
            >
              {scale.value !== 1 && (
                <div class="fixed bottom-8 left-8 z-[10000] rounded-lg bg-[#363A41] px-4 py-1 text-[#BABEC5]">
                  <span class="text-m-h5">{Math.round(scale.value * 100)}%</span>
                </div>
              )}
            </Transition>
          </div>
        )}
      </Transition>
    )
  }
})
